import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { EmptyState, ErrorState } from "@/components/empty-state";
import { useSettings } from "@/lib/settings";

export const Route = createFileRoute("/_site/deals")({
  head: () => ({
    meta: [
      { title: "Best game key deals — NexKey.gg" },
      { name: "description", content: "Today's biggest game key discounts compared against Steam reference prices." },
      { property: "og:title", content: "Best game key deals — NexKey.gg" },
      { property: "og:description", content: "Top discounted game keys updated daily." },
    ],
    links: [{ rel: "canonical", href: "/deals" }],
  }),
  component: DealsPage,
});

function DealsPage() {
  const { formatPrice, t } = useSettings();
  const best = useQuery({ queryKey: ["best-deals-page"], queryFn: () => api.bestDeals() });

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-6">
        <h1 className="font-display text-3xl font-bold">Best deals</h1>
        <p className="mt-1 text-sm text-muted-foreground">Discounts calculated against the official Steam reference price.</p>
      </header>

      {best.isError && <ErrorState message={(best.error as Error).message} />}
      {best.isSuccess && best.data.data.length === 0 && (
        <EmptyState title={t("empty.deals")} description="Add shop offers from the admin dashboard to populate this list." />
      )}
      {best.isSuccess && best.data.data.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {best.data.data.map((o) => (
            <div key={o.id} className="flex items-center gap-4 rounded-xl border border-border bg-card p-3">
              {o.game?.headerImage && <img src={o.game.headerImage} alt="" className="h-20 w-36 rounded-md object-cover" />}
              <div className="flex-1 overflow-hidden">
                <Link to="/game/$slug" params={{ slug: o.game!.slug }} className="line-clamp-1 font-semibold hover:text-primary">{o.game?.title}</Link>
                <div className="text-xs text-muted-foreground">{o.shop.name} · {o.platform || "—"} · {o.region || "—"}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Steam: <span className="text-foreground">{formatPrice(o.game?.steamPriceFinal ?? null, o.game?.steamCurrency)}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="inline-block rounded-md bg-success/20 px-2 py-0.5 text-[10px] font-bold text-success">-{o.savingsPercent}%</div>
                <div className="mt-1 font-display text-lg font-bold text-primary">{formatPrice(o.price, o.currency)}</div>
                <a href={o.affiliateUrl} target="_blank" rel="noopener noreferrer sponsored" className="mt-2 inline-flex items-center gap-1 rounded-md bg-primary-gradient px-3 py-1 text-xs font-semibold text-primary-foreground">
                  {t("offer.viewDeal")} <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
